import { useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, ChevronDown, PenLine } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { fmtParis } from "@/lib/constants";

const LABELS = { technician: "Technicien", client: "Client", depot: "Dépôt (client)", restitution: "Restitution et compte rendu (client)" };
const ORDER = ["depot", "restitution", "client", "technician"];
const ACTIONS = { replaced: "remplacée", removed: "retirée" };

const Row = ({ role, s, onResign }) => (
  <li className="flex flex-wrap items-center gap-2 rounded-lg border border-white/[0.07] px-3 py-2" data-testid={`signature-status-${role}`}>
    {s.modified ? <AlertTriangle className="h-4 w-4 text-amber-300" /> : <CheckCircle2 className="h-4 w-4 text-emerald-300" />}
    <span className="min-w-0 flex-1 text-sm text-white">{LABELS[role]}
      <span className="block text-xs text-slate-400">{s.name} · {fmtParis(s.signed_at)}{s.ref ? ` · réf. ${s.ref}` : ""}</span>
      {s.modified && <span className="block text-xs text-amber-200" data-testid={`signature-modified-${role}`}>Document modifié depuis cette signature : la version actuelle n'est pas validée.</span>}
    </span>
    {s.modified && <Button size="sm" variant="outline" onClick={() => onResign(role)} data-testid={`signature-resign-${role}`}><PenLine className="mr-1 h-4 w-4" />Faire re-signer</Button>}
  </li>
);

const History = ({ id, version }) => {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  useEffect(() => {
    if (open) api.get(`/interventions/${id}/signatures/history`).then((r) => setItems(r.data)).catch(() => setItems([]));
  }, [open, id, version]);
  return (
    <div>
      <button type="button" onClick={() => setOpen(!open)} className="flex items-center gap-1 text-xs text-slate-400 hover:text-white" data-testid="signature-history-toggle">
        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} />Versions précédentes
      </button>
      {open && (
        <ul className="mt-2 space-y-1 text-xs text-slate-400" data-testid="signature-history-list">
          {!items.length && <li>Aucune version archivée.</li>}
          {items.map((h, n) => <li key={n}>{LABELS[h.role] || h.role} · {h.name} · signée {fmtParis(h.signed_at)}{h.ref ? ` · réf. ${h.ref}` : ""} · {ACTIONS[h.action] || h.action} le {fmtParis(h.archived_at)} par {h.archived_by_name}</li>)}
        </ul>
      )}
    </div>
  );
};

export const SignatureStatus = ({ inter, onResign }) => {
  const [status, setStatus] = useState({});
  useEffect(() => {
    api.get(`/interventions/${inter.id}/signatures/status`).then((r) => setStatus(r.data)).catch(() => setStatus({}));
  }, [inter]);
  const roles = ORDER.filter((r) => status[r]);
  const ws = inter.workshop || {};
  if (!roles.length && !ws.returned_unsigned) return null;
  return (
    <section className="space-y-2 rounded-xl border border-white/[0.07] bg-[#122549]/60 p-4" data-testid="signature-status">
      <h2 className="font-display text-base font-semibold text-white">Signatures</h2>
      <ul className="space-y-2">{roles.map((r) => <Row key={r} role={r} s={status[r]} onResign={onResign} />)}</ul>
      {ws.returned_unsigned && !status.restitution && (
        <div className="flex flex-wrap items-center gap-2">
          <p className="flex-1 text-xs text-slate-400" data-testid="signature-unsigned-return">Restitué sans signature du client{ws.returned_unsigned_reason ? ` (${ws.returned_unsigned_reason})` : ""}.</p>
          <Button size="sm" variant="outline" onClick={() => onResign("restitution")} data-testid="signature-sign-restitution-later"><PenLine className="mr-1 h-4 w-4" />Faire signer la restitution</Button>
        </div>
      )}
      <History id={inter.id} version={inter} />
    </section>
  );
};
